// src/components/admin/CreateProductPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import { AdminShell } from './Sidebar';
import { ProductProfilePageRoute } from '../../consts';

const normalizeCategories = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((c) => ({
      id: c.id ?? c.id_product_category ?? c.category_id ?? c.value ?? c.code ?? '',
      name: c.name ?? c.category_name ?? c.label ?? '',
    }))
    .filter((x) => x.id !== '' && x.name !== '');

export default function CreateProductPage() {
  const navigate = useNavigate();
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [categories, setCategories] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [dictLoading, setDictLoading] = useState(true);
  const [dictError, setDictError] = useState('');

  const [form, setForm] = useState({
    name: '', price: '',
    category_name: '', id_product_category: '',
    id_measure: '', description: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const categoryIdByName = (cname) => categories.find((c) => c.name === cname)?.id ?? '';

  useEffect(() => {
    const ctrl = new AbortController();
    async function soft(url) {
      try {
        const r = await fetch(url, { signal: ctrl.signal });
        if (!r.ok) throw new Error();
        return await r.json();
      } catch { return []; }
    }
    (async () => {
      try {
        setDictLoading(true); setDictError('');
        const [catsRaw, measRaw] = await Promise.all([
          soft(`${apiBase}v1/productCategories`),
          soft(`${apiBase}v1/measures?sortBy=id&orderBy=asc`),
        ]);
        setCategories(normalizeCategories(catsRaw));
        setMeasures(Array.isArray(measRaw) ? measRaw : []);
      } catch (e) {
        setDictError(e.message || 'Не удалось загрузить справочники');
      } finally {
        setDictLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [apiBase]);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category_name') {
      const newId = categoryIdByName(value);
      setForm((prev) => ({ ...prev, category_name: value, id_product_category: newId ? String(newId) : '' }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaveError(''); setSaving(true);
    try {
      if (!form.name.trim()) throw new Error('Укажите название товара');
      if (!form.id_product_category) throw new Error('Выберите категорию');
      if (!form.id_measure) throw new Error('Укажите единицу измерения');
      if (form.price === '' || isNaN(Number(form.price))) throw new Error('Цена должна быть числом');
      if (!String(form.description || '').trim()) throw new Error('Укажите описание');
      const body = {
        name: form.name,
        id_product_category: Number(form.id_product_category),
        id_measure: Number(form.id_measure),
        description: form.description,
        price: Number(form.price),
      };
      const res = await fetch(`${apiBase}v1/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${res.statusText} ${t}`.trim());
      }
      const created = await res.json();
      const newId = String(created.id ?? '');
      if (newId) navigate(generatePath(ProductProfilePageRoute, { id: newId }));
      else setForm({ name: '', price: '', category_name: '', id_product_category: '', id_measure: '', description: '' });
    } catch (e) {
      setSaveError(e.message || 'Не удалось создать товар');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell
      breadcrumbs="Товары"
      title="Добавить товар"
      subtitle="Только обязательные поля — остальное редактируется в карточке."
      actions={
        <>
          <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Отмена</button>
          <button
            type="submit"
            form="create-product-form"
            className="adminbtn adminbtn--primary"
            disabled={saving || dictLoading}
          >
            {saving ? 'Сохранение…' : 'Создать товар'}
          </button>
        </>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          {!!dictError && <div className="crmpage__alert crmpage__alert--warning">{dictError}</div>}
          {!!saveError && <div className="crmpage__alert crmpage__alert--error">{saveError}</div>}

          <form id="create-product-form" className="adminform" onSubmit={onSubmit} style={{ maxWidth: 720 }}>
            <section className="adminform__card">
              <div className="adminform__section-title">Новый товар</div>
              <div className="adminform__grid">
                <label className="adminform__field adminform__field--full">
                  <span className="adminform__label">Название товара<span className="req">*</span></span>
                  <input className="adminform__input" name="name" value={form.name} onChange={onChange} required />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Цена, ₸<span className="req">*</span></span>
                  <input type="number" className="adminform__input" name="price" value={form.price} onChange={onChange} placeholder="4500" required />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Категория<span className="req">*</span></span>
                  <select className="adminform__select" name="category_name" value={form.category_name} onChange={onChange} disabled={dictLoading || categories.length === 0} required>
                    <option value="">{dictLoading ? 'Загрузка…' : '— выберите —'}</option>
                    {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Ед. измерения<span className="req">*</span></span>
                  <select className="adminform__select" name="id_measure" value={form.id_measure} onChange={onChange} disabled={dictLoading || measures.length === 0} required>
                    <option value="">{dictLoading ? 'Загрузка…' : '— выберите —'}</option>
                    {measures.map((m) => <option key={m.id} value={String(m.id)}>{m.name ?? `#${m.id}`}</option>)}
                  </select>
                </label>
                <label className="adminform__field adminform__field--full">
                  <span className="adminform__label">Описание<span className="req">*</span></span>
                  <textarea className="adminform__textarea" name="description" value={form.description} onChange={onChange} rows={4} required />
                </label>
              </div>
            </section>
          </form>
        </div>
      </div>
    </AdminShell>
  );
}
